import React, { useState, useEffect, useRef } from 'react';
import quotationService from '../../services/quotationService';
import apiService from '../../services/apiService';
import './ChatbotWidget.css';

interface Message {
  id: string;
  sender: 'bot' | 'user';
  text: string;
  timestamp: string;
  isQuoteCard?: boolean;
  quoteData?: {
    carName: string;
    variantName: string;
    image: string;
    exShowroom: number;
    onRoad: number;
    downPayment: string;
    monthlyEmi: string;
    loanAmount: number;
    roi: number;
  };
}

const FALLBACK_CARS = [
  {
    name: 'Maruti Suzuki Wagon-R',
    image: '/Website-Images/Cars/wagnor.jpg',
    downPayment: '₹92,821',
    monthlyEmi: '₹11,884/month',
    globalExShowroomPrice: 650000,
    globalLoanAmount: 585000,
    globalInterestRate: 8.5,
    globalTotalOnRoadPrice: 715000,
    variants: [
      { name: 'H3 CNG', colors: ['White'] },
      { name: 'LXI CNG', colors: ['White', 'Silver', 'Grey', 'Red', 'Blue'] },
      { name: 'VXI CNG', colors: ['White', 'Silver', 'Grey', 'Red', 'Blue'] }
    ]
  },
  {
    name: 'Maruti Suzuki ERTIGA',
    image: '/Website-Images/Cars/ertiga.jpg',
    downPayment: '₹99,347',
    monthlyEmi: '₹23,367/month',
    globalExShowroomPrice: 1000000,
    globalLoanAmount: 875000,
    globalInterestRate: 8.5,
    globalTotalOnRoadPrice: 1125000,
    variants: [
      { name: 'Tour M CNG 1.5 MT', colors: ['White'] },
      { name: 'VXI CNG 1.5 MT', colors: ['White', 'Silver', 'Grey', 'Red', 'Blue'] },
      { name: 'ZXI CNG 1.5 MT', colors: ['White', 'Silver', 'Grey', 'Red', 'Blue'] }
    ]
  },
  {
    name: 'TOYOTA RUMION',
    image: '/Website-Images/Cars/Ruminum.jpg',
    downPayment: '₹1,03,647',
    monthlyEmi: '₹23,344/month',
    globalExShowroomPrice: 850000,
    globalLoanAmount: 755000,
    globalInterestRate: 8.5,
    globalTotalOnRoadPrice: 950000,
    variants: [
      { name: 'S CNG 1.5 MT', colors: ['White', 'Silver', 'Grey'] }
    ]
  },
  {
    name: 'HYUNDAI AURA',
    image: '/Website-Images/Cars/Aura.jpg',
    downPayment: '₹1,23,821',
    monthlyEmi: '₹15,809/month',
    globalExShowroomPrice: 700000,
    globalLoanAmount: 625000,
    globalInterestRate: 8.5,
    globalTotalOnRoadPrice: 775000,
    variants: [
      { name: 'E CNG', colors: ['White', 'Silver', 'Grey', 'Cherry Night'] },
      { name: 'S CNG', colors: ['White', 'Silver', 'Grey', 'Cherry Night'] },
      { name: 'SX CNG', colors: ['White', 'Silver', 'Grey', 'Cherry Night'] }
    ]
  },
  {
    name: 'Maruti Suzuki Dzire',
    image: '/Website-Images/Cars/Dzire.jpg',
    downPayment: '₹1,35,971',
    monthlyEmi: '₹17,531/month',
    globalExShowroomPrice: 750000,
    globalLoanAmount: 665000,
    globalInterestRate: 8.5,
    globalTotalOnRoadPrice: 850000,
    variants: [
      { name: "Tour's CNG", colors: ['White'] }
    ]
  },
  {
    name: 'Toyota Innova Crysta',
    image: '/Website-Images/Cars/Crysta.jpg',
    downPayment: '₹1,59,547',
    monthlyEmi: '₹38,557/month',
    globalExShowroomPrice: 1800000,
    globalLoanAmount: 1650000,
    globalInterestRate: 8.5,
    globalTotalOnRoadPrice: 1950000,
    variants: [
      { name: 'GX', colors: ['White', 'Silver', 'Pearl White'] },
      { name: 'GX+', colors: ['White', 'Silver', 'Pearl White'] },
      { name: 'VX', colors: ['White', 'Silver', 'Pearl White'] },
      { name: 'ZX', colors: ['White', 'Silver', 'Pearl White'] }
    ]
  }
];

const TRANSLATIONS: Record<string, any> = {
  en: {
    greeting: "Hello! Welcome to ASW Cars. 🚗",
    assistant: "I am your ASW Quote Assistant. I can help you find your dream car and calculate a customized pricing quotation instantly.",
    choose_car: "Which car model are you interested in today?",
    variant_selected: (carName: string) => `Excellent choice! The ${carName} is a fantastic vehicle.`,
    choose_variant: "Please select a variant to see quotation options:",
    variant_desc: (variantName: string) => `Great, looking at the ${variantName} variant.`,
    ask_name: "To calculate your customized quotation (including down payment and EMI breakdowns) and connect you with an executive, what is your full name?",
    nice_to_meet: (name: string) => `Nice to meet you, ${name}!`,
    ask_phone: "Lastly, what is your phone number so we can register this quote in our system?",
    invalid_phone: "That doesn't look like a valid phone number. Please enter a valid 10-digit mobile number.",
    registering: "Got it! Registering your request in our CRM and preparing your instant quotation...",
    success_quote: (name: string) => `Here is your customized quotation, ${name}! 👇`,
    representative_call: (phone: string) => `A dedicated sales executive from ASW Cars will call you shortly on ${phone} to assist with color choices, finance approvals, and test drives.`,
    ex_showroom: "Ex-Showroom Price",
    on_road: "On-Road Price (Est.)",
    loan_amount: "Loan Amount",
    roi: "Rate of Interest (Est.)",
    down_payment: "Down Payment",
    monthly_emi: "Monthly EMI (5 yrs)",
    disclaimer: "Pricing includes CRTM & CRM delivery charges.",
    placeholder_name: "Enter your name...",
    placeholder_phone: "Enter phone number...",
    placeholder_guide: "Chat is guided. Use options above.",
    check_another: "🔄 Check Another Car",
    restart: "Restart Chat / फिर से शुरू करें"
  },
  hi: {
    greeting: "नमस्ते! ASW Cars में आपका स्वागत है। 🚗",
    assistant: "मैं आपका ASW कोट असिस्टेंट हूँ। मैं आपकी पसंदीदा कार ढूँढने और तुरंत कस्टमाइज़्ड कोटेशन तैयार करने में आपकी मदद करूँगा।",
    choose_car: "आज आप किस कार मॉडल में रुचि रखते हैं?",
    variant_selected: (carName: string) => `शानदार विकल्प! ${carName} एक बेहतरीन गाड़ी है।`,
    choose_variant: "कोटेशन देखने के लिए कृपया एक वेरिएंट चुनें:",
    variant_desc: (variantName: string) => `बढ़िया, हम ${variantName} वेरिएंट देख रहे हैं।`,
    ask_name: "आपका कस्टमाइज़्ड कोटेशन (डाउन पेमेंट और EMI डिटेल्स के साथ) तैयार करने के लिए, आपका पूरा नाम क्या है?",
    nice_to_meet: (name: string) => `आपसे मिलकर अच्छा लगा, ${name}!`,
    ask_phone: "आखिर में, अपना फ़ोन नंबर दर्ज करें ताकि हम इस कोट को रजिस्टर कर सकें।",
    invalid_phone: "यह फ़ोन नंबर सही नहीं लग रहा है। कृपया 10 अंकों का मोबाइल नंबर दर्ज करें।",
    registering: "समझ गया! आपके अनुरोध को हमारे CRM में दर्ज किया जा रहा है और कोटेशन तैयार हो रहा है...",
    success_quote: (name: string) => `यहाँ आपका कोटेशन है, ${name}! 👇`,
    representative_call: (phone: string) => `ASW Cars के एक सेल्स अधिकारी जल्द ही आपसे ${phone} पर संपर्क करेंगे ताकि वे आपको कलर ऑप्शंस, लोन अप्रूवल और टेस्ट ड्राइव में मदद कर सकें।`,
    ex_showroom: "एक्स-शोरूम कीमत",
    on_road: "ऑन-रोड कीमत (अनुमानित)",
    loan_amount: "लोन की राशि",
    roi: "ब्याज दर (अनुमानित)",
    down_payment: "डाउन पेमेंट",
    monthly_emi: "मासिक EMI (5 वर्ष)",
    disclaimer: "कीमतों में CRTM और CRM डिलीवरी शुल्क शामिल हैं।",
    placeholder_name: "अपना नाम दर्ज करें...",
    placeholder_phone: "फ़ोन नंबर दर्ज करें...",
    placeholder_guide: "चैट निर्देशित है। ऊपर दिए गए विकल्पों का उपयोग करें।",
    check_another: "🔄 दूसरी गाड़ी देखें",
    restart: "चैट फिर से शुरू करें"
  }
};

const ChatbotWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [typing, setTyping] = useState(false);
  const [cars, setCars] = useState<any[]>(FALLBACK_CARS);
  const userInteractedRef = useRef(false);
  
  // Conversation Flow State
  const [flowState, setFlowState] = useState<'LANGUAGE_SELECTION' | 'GREETING' | 'CAR_SELECTION' | 'VARIANT_SELECTION' | 'ASK_NAME' | 'ASK_PHONE' | 'QUOTE_DISPLAY'>('LANGUAGE_SELECTION');
  const [language, setLanguage] = useState<'en' | 'hi' | null>(null);
  const [selectedCar, setSelectedCar] = useState<any>(null);
  const [selectedVariant, setSelectedVariant] = useState<any>(null);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [inputValue, setInputValue] = useState('');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-open chatbot after 1 minute (60,000 ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!userInteractedRef.current) {
        setIsOpen(true);
      }
    }, 60000);

    return () => clearTimeout(timer);
  }, []);

  // Auto-scroll messages to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, typing]);

  // Fetch cars on startup
  useEffect(() => {
    const fetchCars = async () => {
      try {
        const data = await quotationService.getCarSummaries();
        if (data && data.length > 0) {
          setCars(data);
        }
      } catch (err) {
        console.warn('Chatbot - Could not load car summaries, using fallbacks:', err);
      }
    };
    fetchCars();
  }, []);

  // Initialize language selection when chat opens for the first time
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      triggerLanguagePrompt();
    }
  }, [isOpen]);

  const addBotMessage = (text: string, delay = 800) => {
    setTyping(true);
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        setTyping(false);
        setMessages((prev) => [
          ...prev,
          {
            id: Math.random().toString(),
            sender: 'bot',
            text,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          }
        ]);
        resolve();
      }, delay);
    });
  };

  const addQuoteCardMessage = (quoteData: any, delay = 1000) => {
    setTyping(true);
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        setTyping(false);
        setMessages((prev) => [
          ...prev,
          {
            id: Math.random().toString(),
            sender: 'bot',
            text: '',
            isQuoteCard: true,
            quoteData,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          }
        ]);
        resolve();
      }, delay);
    });
  };

  const addUserMessage = (text: string) => {
    setMessages((prev) => [
      ...prev,
      {
        id: Math.random().toString(),
        sender: 'user',
        text,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }
    ]);
  };

  const triggerLanguagePrompt = async () => {
    setFlowState('LANGUAGE_SELECTION');
    await addBotMessage("Please select your preferred language / कृपया अपनी पसंदीदा भाषा चुनें:");
  };

  const handleLanguageSelect = async (lang: 'en' | 'hi') => {
    addUserMessage(lang === 'en' ? 'English' : 'हिन्दी (Hindi)');
    setLanguage(lang);
    setFlowState('GREETING');
    
    // Welcome sequence in chosen language
    await addBotMessage(TRANSLATIONS[lang].greeting);
    await addBotMessage(TRANSLATIONS[lang].assistant);
    await addBotMessage(TRANSLATIONS[lang].choose_car);
    setFlowState('CAR_SELECTION');
  };

  const handleCarSelect = async (car: any) => {
    addUserMessage(car.name);
    setSelectedCar(car);
    setFlowState('VARIANT_SELECTION');
    const lang = language || 'en';
    await addBotMessage(TRANSLATIONS[lang].variant_selected(car.name));
    await addBotMessage(TRANSLATIONS[lang].choose_variant);
  };

  const handleVariantSelect = async (variant: any) => {
    addUserMessage(variant.name);
    setSelectedVariant(variant);
    setFlowState('ASK_NAME');
    const lang = language || 'en';
    await addBotMessage(TRANSLATIONS[lang].variant_desc(variant.name));
    await addBotMessage(TRANSLATIONS[lang].ask_name);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleTextSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const value = inputValue.trim();
    if (!value) return;

    setInputValue('');
    const lang = language || 'en';

    if (flowState === 'ASK_NAME') {
      addUserMessage(value);
      setCustomerName(value);
      setFlowState('ASK_PHONE');
      await addBotMessage(TRANSLATIONS[lang].nice_to_meet(value));
      await addBotMessage(TRANSLATIONS[lang].ask_phone);
      setTimeout(() => inputRef.current?.focus(), 100);
    } else if (flowState === 'ASK_PHONE') {
      // Basic phone validation
      const phoneRegex = /^[+]?[0-9]{8,15}$/;
      if (!phoneRegex.test(value.replace(/[\s-()]/g, ''))) {
        addUserMessage(value);
        await addBotMessage(TRANSLATIONS[lang].invalid_phone);
        return;
      }

      addUserMessage(value);
      setCustomerPhone(value);
      setFlowState('QUOTE_DISPLAY');
      
      await addBotMessage(TRANSLATIONS[lang].registering);
      
      // Submit Lead to CRM database
      try {
        const notes = `Customer chatbot submission. Interested in ${selectedCar.name} (${selectedVariant.name}). Ex-showroom: ₹${selectedCar.globalExShowroomPrice?.toLocaleString('en-IN') || '—'}.`;
        const payload = {
          customer_name: customerName,
          phone: value,
          source: 'Website Chatbot',
          notes: notes,
          interested_car: `${selectedCar.name} - ${selectedVariant.name}`,
          tags: ['Chatbot', 'QuotationQuery'],
          priority: 'Warm'
        };
        console.log('Chatbot - Submitting Lead payload:', payload);
        await apiService.submitPublicChatbotLead(payload);
      } catch (err) {
        console.error('Chatbot - Lead submission error:', err);
      }

      // Prepare Quotation Data
      const quoteData = {
        carName: selectedCar.name,
        variantName: selectedVariant.name,
        image: selectedCar.image,
        exShowroom: selectedCar.globalExShowroomPrice || 800000,
        onRoad: selectedCar.globalTotalOnRoadPrice || 900000,
        downPayment: selectedCar.downPayment || '₹99,000',
        monthlyEmi: selectedCar.monthlyEmi || '₹18,000/month',
        loanAmount: selectedCar.globalLoanAmount || 700000,
        roi: selectedCar.globalInterestRate || 8.5
      };

      await addBotMessage(TRANSLATIONS[lang].success_quote(customerName));
      await addQuoteCardMessage(quoteData);
      await addBotMessage(TRANSLATIONS[lang].representative_call(value));
    }
  };

  const handleRestart = () => {
    setMessages([]);
    setSelectedCar(null);
    setSelectedVariant(null);
    setCustomerName('');
    setCustomerPhone('');
    setInputValue('');
    setLanguage(null);
    setFlowState('LANGUAGE_SELECTION');
    triggerLanguagePrompt();
  };

  const activeLang = language || 'en';

  return (
    <div className="asw-chatbot-container">
      {/* Floating launcher button */}
      {!isOpen && (
        <button 
          className="asw-chatbot-launcher" 
          onClick={() => {
            userInteractedRef.current = true;
            setIsOpen(true);
          }} 
          aria-label="Open chat"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
          <span className="asw-chatbot-pulse"></span>
        </button>
      )}

      {/* Chat window */}
      {isOpen && (
        <div className="asw-chatbot-window">
          {/* Header */}
          <div className="asw-chatbot-header">
            <div className="asw-chatbot-header-info">
              <div className="asw-chatbot-avatar">💬</div>
              <div>
                <h3 className="asw-chatbot-title">ASW Quote Bot</h3>
                <p className="asw-chatbot-subtitle">
                  <span className="asw-chatbot-status-dot"></span> Online • Auto Quote
                </p>
              </div>
            </div>
            <button 
              className="asw-chatbot-close-btn" 
              onClick={() => {
                userInteractedRef.current = true;
                setIsOpen(false);
              }} 
              aria-label="Close chat"
            >
              ×
            </button>
          </div>

          {/* Messages Area */}
          <div className="asw-chatbot-messages">
            {messages.map((msg) => (
              <div key={msg.id} className={`asw-chatbot-msg ${msg.sender}`}>
                {!msg.isQuoteCard ? (
                  <div className="asw-chatbot-bubble">{msg.text}</div>
                ) : (
                  msg.quoteData && (
                    <div className="asw-chatbot-quote-card">
                      <div className="asw-chatbot-quote-header">
                        <img 
                          src={msg.quoteData.image} 
                          alt={msg.quoteData.carName} 
                          className="asw-chatbot-quote-car-img"
                          onError={(e) => { e.currentTarget.src = '/Website-Images/Cars/placeholder.svg'; }}
                        />
                        <div className="asw-chatbot-quote-car-info">
                          <h4>{msg.quoteData.carName}</h4>
                          <p>{msg.quoteData.variantName}</p>
                        </div>
                      </div>
                      <div className="asw-chatbot-quote-row">
                        <span>{TRANSLATIONS[activeLang].ex_showroom}:</span>
                        <strong>₹{msg.quoteData.exShowroom.toLocaleString('en-IN')}</strong>
                      </div>
                      <div className="asw-chatbot-quote-row">
                        <span>{TRANSLATIONS[activeLang].on_road}:</span>
                        <strong>₹{msg.quoteData.onRoad.toLocaleString('en-IN')}</strong>
                      </div>
                      <div className="asw-chatbot-quote-row">
                        <span>{TRANSLATIONS[activeLang].loan_amount}:</span>
                        <strong>₹{msg.quoteData.loanAmount.toLocaleString('en-IN')}</strong>
                      </div>
                      <div className="asw-chatbot-quote-row">
                        <span>{TRANSLATIONS[activeLang].roi}:</span>
                        <strong>{msg.quoteData.roi}% p.a.</strong>
                      </div>
                      <div className="asw-chatbot-quote-row highlight">
                        <span>{TRANSLATIONS[activeLang].down_payment}:</span>
                        <strong>{msg.quoteData.downPayment}</strong>
                      </div>
                      <div className="asw-chatbot-quote-row highlight">
                        <span>{TRANSLATIONS[activeLang].monthly_emi}:</span>
                        <strong>{msg.quoteData.monthlyEmi}</strong>
                      </div>
                      <div className="asw-chatbot-quote-footer">
                        {TRANSLATIONS[activeLang].disclaimer}
                      </div>
                    </div>
                  )
                )}
                <span className="asw-chatbot-timestamp">{msg.timestamp}</span>
              </div>
            ))}

            {/* Typing Indicator */}
            {typing && (
              <div className="asw-chatbot-msg bot">
                <div className="asw-chatbot-typing">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}

            {/* Interactive Options based on Flow State */}
            {!typing && (
              <div className="asw-chatbot-options-container">
                {flowState === 'LANGUAGE_SELECTION' && (
                  <>
                    <button 
                      className="asw-chatbot-option-btn"
                      onClick={() => handleLanguageSelect('en')}
                    >
                      English
                    </button>
                    <button 
                      className="asw-chatbot-option-btn"
                      onClick={() => handleLanguageSelect('hi')}
                    >
                      हिन्दी (Hindi)
                    </button>
                  </>
                )}

                {flowState === 'CAR_SELECTION' && 
                  cars.map((car) => (
                    <button 
                      key={car.name} 
                      className="asw-chatbot-option-btn"
                      onClick={() => handleCarSelect(car)}
                    >
                      {car.name}
                    </button>
                  ))
                }

                {flowState === 'VARIANT_SELECTION' && selectedCar &&
                  selectedCar.variants.map((v: any) => (
                    <button 
                      key={v.name} 
                      className="asw-chatbot-option-btn"
                      onClick={() => handleVariantSelect(v)}
                    >
                      {v.name}
                    </button>
                  ))
                }

                {flowState === 'QUOTE_DISPLAY' && (
                  <button 
                    className="asw-chatbot-option-btn"
                    onClick={handleRestart}
                  >
                    {TRANSLATIONS[activeLang].check_another}
                  </button>
                )}
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <form className="asw-chatbot-input-area" onSubmit={handleTextSubmit}>
            <input
              type="text"
              ref={inputRef}
              className="asw-chatbot-input"
              placeholder={
                flowState === 'ASK_NAME' 
                  ? TRANSLATIONS[activeLang].placeholder_name 
                  : flowState === 'ASK_PHONE' 
                    ? TRANSLATIONS[activeLang].placeholder_phone 
                    : TRANSLATIONS[activeLang].placeholder_guide
              }
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              disabled={flowState !== 'ASK_NAME' && flowState !== 'ASK_PHONE'}
            />
            <button 
              type="submit" 
              className="asw-chatbot-send-btn"
              disabled={(flowState !== 'ASK_NAME' && flowState !== 'ASK_PHONE') || !inputValue.trim()}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default ChatbotWidget;
